/** UNDE STĂ APARATUL CARE ȚINE DE O PIESĂ DE MOBILIER.
 *
 *  ==========================================================================
 *  O PRIZĂ NU ARE O POZIȚIE, ARE UN MOTIV.
 *
 *  Priza de lângă canapea nu e la (3,42; 1,18): e la capătul canapelei, pe
 *  peretele din spatele ei. Cifra e o CONSECINȚĂ, iar mutând canapeaua se
 *  schimbă consecința, nu motivul. De-aia regula de așezare stă aici, într-un
 *  singur loc, și e citită de amândouă capetele:
 *
 *    · BAGHETA (`auto.ts`) o cheamă o dată, când scrie instalația;
 *    · URMĂRIREA (`editor/model::aparateleUrmeazaMobila`) o cheamă din nou, la
 *      fiecare milimetru cu care piesa e trasă pe pânză.
 *
 *  Dacă ar fi fost două socoteli, s-ar fi despărțit la prima: aparatul pus de
 *  baghetă ar fi stat altfel decât același aparat după ce piesa lui a fost
 *  mutată cu un centimetru și adusă înapoi.
 *
 *  CADRUL PIESEI E SINGURA SURSĂ A DIRECȚIILOR. În bibliotecă fața fiecărei
 *  piese e spre `+y` (v. nota din `mobilare.ts`), deci spatele ei — cel care
 *  stă la perete — e `−y`, iar lateralele sunt `±x`. Totul se citește din
 *  `rot_deg` și din oglindiri, nimic nu se declară: un pat întors cu mâna își
 *  duce prizele cu el, fiindcă ele se socotesc din unghiul lui.
 *
 *  DE CE NU SE IA PUR ȘI SIMPLU CEL MAI APROPIAT PERETE. Fiindcă pentru capul
 *  patului nu e cel mai apropiat: într-un dormitor în L, colțul care intră e
 *  mai aproape de noptieră decât peretele tăbliei. Deci se trage o rază din
 *  piesă, ÎNAPOI, și se ia peretele pe care-l lovește — iar «cel mai apropiat»
 *  rămâne doar pentru piesele care nu stau cu spatele la nimic (o masă în
 *  mijlocul camerei).
 *  ==========================================================================
 */
import { inInel, type PiesaDoc, type Pt } from "../doc";

/** De care parte a piesei stă aparatul. Se scrie în document lângă legătură,
 *  fiindcă din geometrie nu se poate deduce: două prize de o parte și de alta
 *  a patului sunt același motiv văzut în oglindă, iar una singură «lângă
 *  canapea» n-are cum să spună singură pe care capăt a fost pusă. */
export type Parte = "stanga" | "dreapta" | "spate";

/** Piesa, cum o vede așezarea: centrul, măsurile ei și cum e întoarsă. */
export type PiesaAsezata = {
  c: Pt;
  /** Lățimea, pe latura lungă (axa `x` a piesei). */
  w: number;
  /** Adâncimea, de la spate la față (axa `y`). */
  h: number;
  deg: number;
  oglX?: boolean;
  oglY?: boolean;
};

/** Piesa din document, cum o citește regula de așezare. Măsurile lipsă iau
 *  0,60 m: e mărimea unui corp de mobilier obișnuit, iar singurul lucru pe care
 *  îl atinge e cât de departe de mijlocul piesei cade priza. */
export const caPiesa = (f: PiesaDoc): PiesaAsezata => ({
  c: [f.x.a, f.y.a],
  w: f.w_m ?? 0.6,
  h: f.h_m ?? 0.6,
  deg: f.rot_deg ?? 0,
  oglX: f.flip_x === true,
  oglY: f.flip_y === true,
});

/** Un perete, cum îl vede ferirea de beton: conturul lui și din ce e. */
export type ZidMaterial = { inel: readonly Pt[]; beton: boolean };

/** Un gol, ca segment între tocuri — acolo nu se montează nimic. */
export type GolSimplu = { a: Pt; b: Pt };

const dist = (p: Pt, q: Pt) => Math.hypot(q[0] - p[0], q[1] - p[1]);

/** CÂT LOC SE LASĂ LÂNGĂ PIESĂ.
 *
 *  0,12 m de la muchia ei. Nu e o cifră de normă, e lățimea unei mâini: destul
 *  cât doza să nu cadă în spatele tăbliei sau al brațului canapelei, și puțin
 *  destul cât priza să rămână «a piesei» și nu una oarecare de pe perete. */
export const MARGINE_LATERALA = 0.12;

/** CÂND «ÎN SPATE» NU MAI ÎNSEAMNĂ NIMIC.
 *
 *  Peste 1,20 m de perete, o piesă nu mai stă cu spatele la el: e o masă în
 *  mijlocul camerei sau o canapea trasă în living. Atunci raza înapoi ar duce
 *  priza pe zidul de peste cameră, la trei metri de piesa care o justifică —
 *  deci se cade pe cel mai apropiat perete, care e chiar răspunsul bun acolo. */
const PRAG_SPATE = 1.2;

// ------------------------------------------------------- cadrul unei piese ---

/** Versorii piesei: lateral (`+x` al ei) și înapoi (`−y` al ei), în lume.
 *
 *  OGLINDIRILE SE APLICĂ DUPĂ ROTIRE, ca în document (v. `PiesaDoc::flip_x`):
 *  o piesă oglindită stânga-dreapta își schimbă stânga cu dreapta, iar o priză
 *  «pe stânga» trebuie să treacă odată cu ea — altfel oglindirea unui colț de
 *  canapea ar lăsa priza de partea în care acum stă brațul. */
export function cadrulPiesei(p: PiesaAsezata): { lateral: Pt; inapoi: Pt } {
  const rad = (p.deg * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const sx = p.oglX ? -1 : 1;
  const sy = p.oglY ? -1 : 1;
  return {
    lateral: [cos * sx, sin * sx],
    // Fața e `+y` rotit: (−sin, cos). Înapoi e opusul ei.
    inapoi: [sin * sy, -cos * sy],
  };
}

/** PUNCTUL DIN CARE SE CAUTĂ PERETELE — colțul din spate al piesei, pe partea
 *  cerută. Nu centrul ei: o priză socotită din centrul unui pat de doi metri ar
 *  ieși chiar sub mijlocul tăbliei, adică exact unde nu se pune niciodată. */
export function ancora(p: PiesaAsezata, parte: Parte): Pt {
  const { lateral, inapoi } = cadrulPiesei(p);
  const s = parte === "stanga" ? -1 : parte === "dreapta" ? 1 : 0;
  const lat = s * (p.w / 2 + MARGINE_LATERALA);
  return [
    p.c[0] + inapoi[0] * (p.h / 2) + lateral[0] * lat,
    p.c[1] + inapoi[1] * (p.h / 2) + lateral[1] * lat,
  ];
}

// -------------------------------------------------------------- pe conturul ---

/** LIPIREA DE PERETE: cel mai apropiat punct de pe conturul camerei, și încotro
 *  privește aparatul de acolo.
 *
 *  Un aparat electric nu plutește: stă pe un zid. Rețetele spun «lângă pat» sau
 *  «peste blat», adică dau un punct din cameră; aici punctul ăla se duce pe
 *  peretele cel mai apropiat de el. Rotirea e a NORMALEI SPRE INTERIOR, ca
 *  simbolul să se citească dinspre cameră, nu dinspre zid. */
export function peZid(p: Pt, inel: readonly Pt[]): { q: Pt; deg: number } {
  let q: Pt = p;
  let cea = Infinity;
  let n: Pt = [0, 1];
  for (let i = 0; i < inel.length; i++) {
    const a = inel[i];
    const b = inel[(i + 1) % inel.length];
    const vx = b[0] - a[0];
    const vy = b[1] - a[1];
    const L2 = vx * vx + vy * vy;
    const t = L2 < 1e-12 ? 0
      : Math.max(0, Math.min(1, ((p[0] - a[0]) * vx + (p[1] - a[1]) * vy) / L2));
    const c: Pt = [a[0] + vx * t, a[1] + vy * t];
    const d = dist(p, c);
    if (d < cea) {
      cea = d;
      q = c;
      const L = Math.hypot(vx, vy) || 1;
      // Normala care intră în cameră, nu cea care iese.
      const cand: Pt = [-vy / L, vx / L];
      n = inInel([c[0] + cand[0] * 0.12, c[1] + cand[1] * 0.12], inel)
        ? cand : [-cand[0], -cand[1]];
    }
  }
  return { q, deg: (Math.atan2(n[1], n[0]) * 180) / Math.PI };
}

/** PE ZIDUL DINTR-O ANUME DIRECȚIE, nu pe cel mai apropiat.
 *
 *  DE CE E NEVOIE ȘI DE ASTA. `peZid` ia cel mai apropiat punct de pe contur, și
 *  e răspunsul corect pentru ce stă deja lipit de un perete — un blat, o
 *  comodă. Pentru priza de la capul patului NU e: peretele care contează e cel
 *  din spatele tăbliei, oricât de departe ar fi.
 *
 *  Măsurat pe M8, dormitorul în L de 13,4 m²: noptiera de la (2,52; 3,60) are
 *  colțul intrând al camerei la 0,40 m și zidul de la capul patului la 0,83 m.
 *  «Cel mai apropiat» punea priza în colțul ăla — adică pe alt perete decât
 *  patul, la un metru de unde trebuie, într-un loc în care nimeni n-ar căuta-o.
 *
 *  Deci se trage o rază din piesă, pe direcția cerută, și se ia prima latură pe
 *  care o taie. Fără tăiere — raza iese pe unde a intrat, pe o geometrie
 *  stricată — se întoarce `null`, iar apelantul cade înapoi pe `peZid`: o priză
 *  pusă cam pe-acolo e mai bună decât una lipsă. */
export function peZidSpre(p: Pt, dir: Pt, inel: readonly Pt[]): {
  q: Pt; deg: number;
} | null {
  const L = Math.hypot(dir[0], dir[1]);
  if (L < 1e-9) return null;
  const dx = dir[0] / L;
  const dy = dir[1] / L;
  let cel: { q: Pt; t: number; n: Pt } | null = null;
  for (let i = 0; i < inel.length; i++) {
    const a = inel[i];
    const b = inel[(i + 1) % inel.length];
    const ex = b[0] - a[0];
    const ey = b[1] - a[1];
    // Raza p + t·d taie muchia a + s·e: sistem de două ecuații.
    const det = dx * -ey - dy * -ex;
    if (Math.abs(det) < 1e-12) continue;                // paralele
    const t = ((a[0] - p[0]) * -ey - (a[1] - p[1]) * -ex) / det;
    const sp = (dx * (a[1] - p[1]) - dy * (a[0] - p[0])) / det;
    if (t <= 1e-6 || sp < -1e-9 || sp > 1 + 1e-9) continue;
    if (cel && t >= cel.t) continue;
    const eL = Math.hypot(ex, ey) || 1;
    cel = { q: [p[0] + dx * t, p[1] + dy * t], t, n: [ey / eL, -ex / eL] };
  }
  if (!cel) return null;
  // Normala care intră în cameră: cea opusă direcției în care s-a tras raza.
  const n: Pt = cel.n[0] * dx + cel.n[1] * dy > 0
    ? [-cel.n[0], -cel.n[1]] : cel.n;
  return { q: cel.q, deg: (Math.atan2(n[1], n[0]) * 180) / Math.PI };
}

// ---------------------------------------------------------- unde NU se pune ---

/** Distanța de la un punct la conturul unui zid — 0 dacă e chiar în el. */
function panaLaZid(p: Pt, inel: readonly Pt[]): number {
  if (inInel(p, inel)) return 0;
  let cea = Infinity;
  for (let i = 0; i < inel.length; i++) {
    const a = inel[i];
    const b = inel[(i + 1) % inel.length];
    const vx = b[0] - a[0];
    const vy = b[1] - a[1];
    const L2 = vx * vx + vy * vy;
    const t = L2 < 1e-12 ? 0
      : Math.max(0, Math.min(1, ((p[0] - a[0]) * vx + (p[1] - a[1]) * vy) / L2));
    cea = Math.min(cea, dist(p, [a[0] + vx * t, a[1] + vy * t]));
  }
  return cea;
}

/** CÂT DE APROAPE DE UN ZID ÎNSEAMNĂ «PE EL».
 *
 *  0,35 m. Aparatul stă pe FAȚA zidului, iar fața e la o jumătate de grosime de
 *  contur — 10–12 cm la zidurile noastre —, deci pragul trebuie să treacă peste
 *  atât fără să prindă și zidul de vizavi al unui perete de 8 cm. */
const RAZA_ZID = 0.35;

/** Aparatul ar cădea pe un perete de BETON?
 *
 *  DE CE CONTEAZĂ. Un perete de beton e structură: în el nu se taie șanț pentru
 *  doză fără acordul structuristului, iar în practică electricianul fie îl
 *  ocolește, fie trage aparent. Un perete de zidărie se taie fără să întrebe
 *  nimeni. Deci, la egalitate de motive, aparatul se duce pe zidărie — «dacă se
 *  poate», nu cu orice preț: v. `fereste`. */
/*  Măsurat pe M2/M5/M8, cu instalația pusă de baghetă: din 45/103/59 de
 *  aparate cădeau pe beton 11/21/13 fără ferire și 9/17/13 cu ea. Restul stau
 *  pe pereți de beton care n-au alternativă la o jumătate de metru — fațada și
 *  calcanul unei bare de locuințe sunt de beton pe toată lungimea lor —, iar
 *  acolo răspunsul corect e să se vadă pe planșă, nu să fie mutat aiurea. */
export function pePeretDeBeton(q: Pt, ziduri: readonly ZidMaterial[]): boolean {
  let cel: ZidMaterial | null = null;
  let cea = RAZA_ZID;
  for (const z of ziduri) {
    const d = panaLaZid(q, z.inel);
    if (d < cea) { cea = d; cel = z; }
  }
  return !!cel?.beton;
}

/** CÂT DE APROAPE DE UN GOL SE POATE MONTA. 0,15 m de toc — atât ține glaful,
 *  și sub el doza ar cădea chiar în golul ușii. */
const LANGA_GOL = 0.15;

/** Aparatul ar cădea într-o ușă sau într-o fereastră? */
export function inGol(q: Pt, goluri: readonly GolSimplu[]): boolean {
  for (const g of goluri) {
    const vx = g.b[0] - g.a[0];
    const vy = g.b[1] - g.a[1];
    const L2 = vx * vx + vy * vy;
    const t = L2 < 1e-12 ? 0
      : Math.max(0, Math.min(1, ((q[0] - g.a[0]) * vx + (q[1] - g.a[1]) * vy) / L2));
    if (dist(q, [g.a[0] + vx * t, g.a[1] + vy * t]) < LANGA_GOL) return true;
  }
  return false;
}

/** APARATUL E AL CAMEREI ĂSTEIA?
 *
 *  NU se întreabă cu punctul lui: aparatul stă CHIAR PE conturul camerei, iar
 *  «e punctul în poligon» pentru un punct de pe muchie răspunde ce vrea —
 *  aruncarea de rază cade pe muchie și iese odată într-o parte, odată în alta.
 *  Din cauza asta o priză putea fi în același timp a nimănui și a două camere.
 *
 *  Se întreabă cu o palmă ÎNAINTEA lui, pe direcția în care privește: un aparat
 *  de perete privește spre camera în care se montează (`rot_deg` e chiar normala
 *  spre interior), deci punctul ăla cade fără ambiguitate înăuntru. O
 *  plafonieră, care privește oricum, e oricum departe de pereți. */
export function aparatulEAlCamerei(
  c: Pt, deg: number, inel: readonly Pt[],
): boolean {
  const rad = (deg * Math.PI) / 180;
  return inInel([c[0] + Math.cos(rad) * 0.12, c[1] + Math.sin(rad) * 0.12], inel);
}

// ------------------------------------------------- mutarea de-a lungul zidului ---

/** Perimetrul inelului, ca listă de muchii cu lungimea lor cumulată. */
function perimetrul(inel: readonly Pt[]): { L: number; capete: number[] } {
  const capete: number[] = [];
  let L = 0;
  for (let i = 0; i < inel.length; i++) {
    L += dist(inel[i], inel[(i + 1) % inel.length]);
    capete.push(L);
  }
  return { L, capete };
}

/** Unde cade `q` pe perimetru, ca lungime de la primul vârf. */
function laPerimetru(q: Pt, inel: readonly Pt[]): number {
  const { capete } = perimetrul(inel);
  let cea = Infinity;
  let s = 0;
  for (let i = 0; i < inel.length; i++) {
    const a = inel[i];
    const b = inel[(i + 1) % inel.length];
    const vx = b[0] - a[0];
    const vy = b[1] - a[1];
    const L2 = vx * vx + vy * vy;
    const t = L2 < 1e-12 ? 0
      : Math.max(0, Math.min(1, ((q[0] - a[0]) * vx + (q[1] - a[1]) * vy) / L2));
    const c: Pt = [a[0] + vx * t, a[1] + vy * t];
    const d = dist(q, c);
    if (d < cea) { cea = d; s = (i ? capete[i - 1] : 0) + t * Math.hypot(vx, vy); }
  }
  return s;
}

/** Punctul de pe perimetru la lungimea `s`, cu normala lui spre interior. */
function pePerimetru(inel: readonly Pt[], s: number): { q: Pt; deg: number } {
  const { L } = perimetrul(inel);
  let u = ((s % L) + L) % L;
  for (let i = 0; i < inel.length; i++) {
    const a = inel[i];
    const b = inel[(i + 1) % inel.length];
    const len = dist(a, b);
    if (u > len && i < inel.length - 1) { u -= len; continue; }
    const t = len < 1e-9 ? 0 : Math.min(1, u / len);
    const q: Pt = [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
    const vx = (b[0] - a[0]) / (len || 1);
    const vy = (b[1] - a[1]) / (len || 1);
    const cand: Pt = [-vy, vx];
    const n = inInel([q[0] + cand[0] * 0.12, q[1] + cand[1] * 0.12], inel)
      ? cand : ([-cand[0], -cand[1]] as Pt);
    return { q, deg: (Math.atan2(n[1], n[0]) * 180) / Math.PI };
  }
  return { q: inel[0], deg: 0 };
}

/** CÂT SE CAUTĂ ÎN LATERAL un loc mai bun.
 *
 *  0,50 m, și pe ACELAȘI perete. Cifra nu e o toleranță, e o măsură de sens: la
 *  atât se ocolește un stâlp înecat în zidărie sau un capăt de diafragmă — adică
 *  chiar cazul pentru care se ferește —, iar mai departe de atât priza nu mai e
 *  a piesei ei. O priză de noptieră mutată un metru și jumătate «ca să nu fie pe
 *  beton» e o priză care nu mai ajunge la noptieră, deci un răspuns mai prost
 *  decât cel de pe beton: acela cel puțin se vede pe planșă și se discută.
 *
 *  Măsurat pe M5: fără oprirea la colț, priza de la capul unui pat aluneca de pe
 *  zidul tăbliei pe fațadă, la 1,03 m — pe alt perete decât cel la care stă
 *  patul, adică fix greșeala pentru care există `peZidSpre`. */
const CAUTARE_LATERALA = 0.5;
const PAS_CAUTARE = 0.05;

/** CÂT SE CAUTĂ CA SĂ NU RĂMÂI ÎN UȘĂ SAU ÎN FEREASTRĂ.
 *
 *  1,00 m — dublul căutării de beton, și tot pe ACELAȘI perete. Diferența
 *  dintre cele două praguri e chiar diferența dintre cele două reguli: betonul
 *  se ocolește «dacă se poate», iar dacă nu se poate aparatul rămâne pe el și
 *  se vede pe planșă, ceea ce e un răspuns; golul NU se poate — sub o fereastră
 *  doza cade în glaf, în ușă cade în toc, iar pe planșă simbolul iese peste
 *  deschidere. Acolo unde alternativa e APARATUL LIPSĂ merită căutat de două
 *  ori mai departe: o priză la 0,80 m de piesa ei tot e priza piesei, una
 *  ștearsă nu mai e nimic. */
const CAUTARE_DIN_GOL = 1.0;

/** Două normale sunt ale aceluiași perete? Sub 6°: un contur transcris dintr-o
 *  planșă n-are laturile chiar paralele. */
const acelasiZid = (a: number, b: number): boolean => {
  const d = ((b - a) % 360 + 360) % 360;
  return Math.min(d, 360 - d) <= 6;
};

/** CE STĂ ÎN FAȚA UNEI PRIZE ȘI O FACE INUTILĂ: un dreptunghi oarecare, dat
 *  prin colțurile lui. Dulapul lipit de perete, sau sfertul de cerc al unei
 *  uși care se deschide peste el. */
export type Blocaj = readonly Pt[];

/** Punctul cade într-unul din blocaje? */
export function eBlocat(q: Pt, blocaje: readonly Blocaj[]): boolean {
  for (const b of blocaje) if (b.length >= 3 && inInel(q, b)) return true;
  return false;
}

/** FERIREA DE BETON ȘI DE GOLURI, prin alunecare pe ACELAȘI perete.
 *
 *  ==========================================================================
 *  DOUĂ REGULI, NU UNA — și tocmai de-aia răspunsul poate lipsi.
 *
 *  · GOLUL E O INTERDICȚIE. Nicio doză în ușă și niciuna în fereastră: acolo
 *    nu e perete, e toc și glaf, iar pe planșă simbolul iese peste deschidere,
 *    adică peste singurul loc din care se citește că acolo se intră. Dacă în
 *    `razaGol` nu există niciun loc liber, se întoarce `null` — «nu se pune»,
 *    și cine cheamă NU scrie aparatul. O priză lipsă se vede și se adaugă cu
 *    mâna; una desenată pe geam se și execută.
 *
 *  · BETONUL E O PREFERINȚĂ. În el nu se taie șanț fără structurist, deci la
 *    egalitate de locuri se alege zidăria — dar numai în `raza`, și numai dacă
 *    locul e oricum liber de goluri. Neputând, aparatul RĂMÂNE pe beton: acolo
 *    se vede pe planșă și se discută. V. `pePeretDeBeton`.
 *
 *  Se pleacă de la locul pe care l-a dat regula și se caută în stânga și în
 *  dreapta lui, din 5 în 5 cm, alternând părțile — deci candidații ies în
 *  ordinea depărtării de locul cerut, iar primul bun e și cel mai aproape de
 *  motivul lui. Căutarea într-o direcție se oprește la colț: dincolo de el e
 *  alt perete, iar o priză de noptieră de după colț nu mai e a noptierei.
 *  ==========================================================================
 */
export function fereste(
  q: Pt, deg: number, inel: readonly Pt[],
  ziduri: readonly ZidMaterial[], goluri: readonly GolSimplu[],
  raza = CAUTARE_LATERALA, razaGol = CAUTARE_DIN_GOL,
  blocaje: readonly Blocaj[] = [],
): { q: Pt; deg: number } | null {
  /** Locurile fără gol, în ordinea depărtării — cât s-a apucat să se strângă
   *  până la primul loc CURAT. Se ține lista fiindcă, negăsindu-se niciunul
   *  curat, răspunsul e cel mai apropiat loc fără gol, adică unul de pe beton. */
  const libere: { q: Pt; deg: number; d: number }[] = [];
  /** Locul e curat — fără gol ȘI fără beton —, deci căutarea se poate opri? */
  const curat = (c: { q: Pt; deg: number; d: number }): boolean => {
    if (inGol(c.q, goluri)) return false;
    libere.push(c);
    // CE E ÎN FAȚA EI SE TRATEAZĂ CA BETONUL, nu ca golul: se alunecă pe
    // perete până iese de sub dulap sau de sub foaia ușii, dar dacă nu e
    // nicăieri loc, priza RĂMÂNE. O priză după dulap se vede pe planșă și se
    // mută; una care nu s-a pus nu se vede deloc. Cerut pe 19.10.2026: «for
    // the normal prize, they should not be blocked by furniture or the door».
    return c.d <= raza + 1e-9 && !pePeretDeBeton(c.q, ziduri)
      && !eBlocat(c.q, blocaje);
  };

  // Drumul obișnuit — locul cerut e deja bun — se plătește cu două întrebări,
  // nu cu o plimbare pe tot peretele: urmărirea din editor trece pe aici la
  // fiecare cadru al unui gest.
  if (curat({ q, deg, d: 0 })) return { q, deg };

  const s0 = laPerimetru(q, inel);
  const viu = [true, true];
  for (let d = PAS_CAUTARE; d <= Math.max(raza, razaGol) + 1e-9; d += PAS_CAUTARE) {
    for (let k = 0; k < 2; k++) {
      if (!viu[k]) continue;
      const c = pePerimetru(inel, s0 + (k ? -d : d));
      if (!acelasiZid(deg, c.deg)) { viu[k] = false; continue; }  // s-a trecut de colț
      if (curat({ ...c, d })) return { q: c.q, deg: c.deg };
    }
    if (!viu[0] && !viu[1]) break;
  }
  return libere.find((c) => c.d <= razaGol + 1e-9) ?? null;
}

// -------------------------------------------------------------- așezarea ---

/** AȘEZAREA UNUI APARAT LÂNGĂ PIESA LUI — regula, într-un singur loc.
 *
 *  Trei pași, toți necesari:
 *    1. ANCORA: colțul din spate al piesei, pe partea cerută (v. `ancora`);
 *    2. PERETELE: cel din spatele piesei, dacă piesa chiar stă cu spatele la
 *       unul (v. `PRAG_SPATE`); altfel cel mai apropiat de ancoră;
 *    3. FERIREA de beton și de goluri, prin alunecare pe același perete.
 *
 *  `null` înseamnă CĂ PE PARTEA ASTA NU SE POATE: tot peretele din spatele
 *  piesei, pe un metru, e ușă sau fereastră. Nu e o eroare, e un răspuns — cine
 *  cheamă ori încearcă altă parte (v. `auto::parteaLibera`), ori nu pune nimic.
 */
export function asezareaLangaPiesa(
  piesa: PiesaAsezata, parte: Parte, inel: readonly Pt[],
  ziduri: readonly ZidMaterial[] = [], goluri: readonly GolSimplu[] = [],
): { q: Pt; deg: number } | null {
  const p = ancora(piesa, parte);
  const { inapoi } = cadrulPiesei(piesa);
  const spre = peZidSpre(p, inapoi, inel);
  const ales = spre && dist(p, spre.q) <= PRAG_SPATE ? spre : peZid(p, inel);
  return fereste(ales.q, ales.deg, inel, ziduri, goluri);
}

// ------------------------------------------------------ două prize alături ---

/** Un aparat de desfăcut: unde stă, cât e de lat și dacă are voie să se miște. */
export type DeDesfacut = {
  q: Pt;
  deg: number;
  /** Lățimea simbolului, în metri — `FelAparat::d`. */
  d: number;
  /** Poate fi împins? Ce a pus omul cu mâna, nu. */
  mobil: boolean;
};

/** Jocul dintre două aparate alăturate. 1 cm: ele CHIAR se montează lipite, în
 *  rame comune, iar pe planșă asta se vede ca două simboluri care se ating. */
const JOC = 0.01;

/** CÂT SE SARE PESTE O DESCHIDERE ca să nu se calce două simboluri. 1,20 m —
 *  cât o ușă de intrare cu tocul ei. Mai mult n-are rost: dacă nici dincolo de
 *  o ușă întreagă nu e loc, peretele ăla e ocupat, iar aparatele rămân cum
 *  sunt. */
const SARE_GOLUL = 1.2;

/** DOUĂ APARATE NU STAU UNUL PESTE ALTUL.
 *
 *  «Pot fi lipite dacă sunt foarte apropiate» — exact asta face: aparatele care
 *  cad pe același perete, la mai puțin de o lățime de simbol unul de altul, se
 *  împing pe lungul zidului până se ating, în loc să se suprapună. Se împing
 *  doar cele MOBILE — cele puse de baghetă —, fiindcă un aparat pus cu mâna e
 *  o decizie, iar o decizie nu se dă la o parte de o regulă.
 *
 *  Gruparea e pe perete: aceeași normală (sub 6°) și aceeași linie (decalaj
 *  perpendicular sub 5 cm). Două prize de o parte și de alta a unui colț au
 *  normale diferite, deci nu se împing una pe alta — și bine fac, fiindcă între
 *  ele e un zid.
 *
 *  ÎMPINSUL NU ARE VOIE SĂ BAGE APARATUL ÎN UȘĂ. De-aia primește `goluri`: el
 *  mută pe lungul zidului, adică exact pe direcția pe care stau deschiderile,
 *  iar interdicția din `fereste` ar fi fost degeaba dacă ultimul pas o călca.
 *  Măsurat pe M5, cu instalația pusă de baghetă: din 8 aparate care cădeau în
 *  goluri, 6 ajunseseră acolo ÎMPINSE, nu așezate.
 *
 *  Iar răspunsul nu e «atunci nu mai împing». Două simboluri lăsate unul peste
 *  altul ca să nu intre în ușă sunt tot un desen greșit, doar în alt fel: pe M5
 *  un comutator și o priză rămâneau la un centimetru unul de altul. Deci se
 *  împinge MAI DEPARTE — peste deschidere, până la primul loc liber —, și abia
 *  dacă nici asta nu se poate se încearcă celălalt aparat al perechii, în sens
 *  invers. Nemergând niciuna, se lasă cum sunt: suprapunerea se vede pe planșă
 *  și se repară cu mâna, doza din toc nu.
 */
export function desfaSuprapunerile(
  lista: DeDesfacut[], goluri: readonly GolSimplu[] = [],
): DeDesfacut[] {
  const out = lista.map((x) => ({ ...x }));
  const luat = new Array<boolean>(out.length).fill(false);
  for (let i = 0; i < out.length; i++) {
    if (luat[i]) continue;
    const rad = (out[i].deg * Math.PI) / 180;
    // Tangenta peretelui: normala rotită cu 90°.
    const tx = -Math.sin(rad);
    const ty = Math.cos(rad);
    const grup = [i];
    luat[i] = true;
    for (let j = i + 1; j < out.length; j++) {
      if (luat[j]) continue;
      const dd = ((out[j].deg - out[i].deg) % 360 + 360) % 360;
      if (Math.min(dd, 360 - dd) > 6) continue;
      const dx = out[j].q[0] - out[i].q[0];
      const dy = out[j].q[1] - out[i].q[1];
      // Decalajul perpendicular pe zid: pe normală.
      if (Math.abs(dx * Math.cos(rad) + dy * Math.sin(rad)) > 0.05) continue;
      grup.push(j);
      luat[j] = true;
    }
    if (grup.length < 2) continue;
    // Pe lungul zidului, în ordine.
    const cu = grup.map((k) => ({
      k, s: out[k].q[0] * tx + out[k].q[1] * ty,
    })).sort((a, b) => a.s - b.s);
    for (let n = 1; n < cu.length; n++) {
      const a = out[cu[n - 1].k];
      const b = out[cu[n].k];
      const minim = (a.d + b.d) / 2 + JOC;
      const gol = cu[n].s - cu[n - 1].s;
      if (gol >= minim - 1e-9) continue;
      const imping = minim - gol;
      /** Cât trebuie împins aparatul, în sensul cerut, ca să nu se mai calce cu
       *  vecinul ȘI să nu cadă în deschidere: strictul necesar, iar dacă acolo
       *  e gol, dincolo de el. `null` — nici peste `SARE_GOLUL` nu s-a ieșit. */
      const cat = (x: DeDesfacut, semn: number): number | null => {
        if (!x.mobil) return null;
        for (let d = imping; d <= imping + SARE_GOLUL + 1e-9; d += PAS_CAUTARE) {
          if (!inGol([x.q[0] + tx * semn * d, x.q[1] + ty * semn * d], goluri)) {
            return d;
          }
        }
        return null;
      };
      const catB = cat(b, 1);
      const catA = catB == null ? cat(a, -1) : null;
      if (catB != null) {
        b.q = [b.q[0] + tx * catB, b.q[1] + ty * catB];
        cu[n].s += catB;
      } else if (catA != null) {
        a.q = [a.q[0] - tx * catA, a.q[1] - ty * catA];
        cu[n - 1].s -= catA;
      }
    }
  }
  return out;
}
